import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { memberService } from '@/services/member-service';
import type { Invitation, Member } from '@/types/core/member';
import type { Page } from '@/types/shared/page';
import { pollForResource } from '@/utils/poll';
import { type UUID, isNilUUID, newUUID } from '@/utils/uuid';

import { memberMutationKeys, memberQueryKeys } from './query-keys';

/**
 * Hook to invite a member to a tenant.
 * Returns a react-query mutation that accepts `{ email: string }`.
 * Applies optimistic updates to the invitations cache.
 */
export function useInviteMember(tenantId?: UUID) {
  const qc = useQueryClient();

  type Context = {
    previousData?: Array<
      [
        queryKey: readonly unknown[],
        data: InfiniteData<Page<Invitation>> | undefined,
      ]
    >;
  };

  return useMutation<
    Invitation | Member | undefined,
    Error,
    { email: string },
    Context
  >({
    mutationKey: memberMutationKeys.inviteMember(tenantId),
    mutationFn: async (payload: { email: string }) => {
      if (!tenantId) throw new Error('Missing tenant id');
      const invitationId = newUUID();
      const result = await memberService.inviteMember(tenantId, invitationId, {
        email: payload.email,
      });

      // Backend returns either:
      //   - invitation_id (non-nil) → new user, invitation was created → poll for invitation
      //   - invitation_id of all zeros (nil UUID) → existing user, auto-added as member → poll for member
      //   - user_id only → existing user, auto-added as member → poll for member
      const hasRealInvitationId =
        result?.invitationId && !isNilUUID(result.invitationId);

      if (hasRealInvitationId) {
        const invitation = await pollForResource(
          async () => {
            const inv = await memberService.findInvitation(
              tenantId,
              result.invitationId!
            );
            return inv?.status === 'sent' ? inv : null;
          },
          { maxAttempts: 10, delayMs: 300 }
        );
        return (
          invitation ?? {
            invitationId: result.invitationId!,
            email: payload.email,
            status: 'pending',
          }
        );
      }
      if (result?.userId) {
        const member = await pollForResource(
          () => memberService.findMember(tenantId, result.userId!),
          { maxAttempts: 10, delayMs: 300 }
        );
        return member ?? { userId: result.userId! };
      }

      return undefined;
    },
    onMutate: async (payload: { email: string }) => {
      if (!tenantId) return { previousData: [] };

      await qc.cancelQueries({
        queryKey: memberQueryKeys.invitationListByTenant(tenantId),
      });

      const previousData = qc.getQueriesData<InfiniteData<Page<Invitation>>>({
        queryKey: memberQueryKeys.invitationListByTenant(tenantId),
      });

      const optimisticInvitation: Invitation = {
        email: payload.email,
        status: 'pending',
      };

      qc.setQueriesData<InfiniteData<Page<Invitation>>>(
        { queryKey: memberQueryKeys.invitationListByTenant(tenantId) },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page, index) =>
              index === 0
                ? {
                    ...page,
                    items: [optimisticInvitation, ...page.items],
                    total: (page.total ?? 0) + 1,
                  }
                : page
            ),
          };
        }
      );

      return { previousData };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: async (result) => {
      if (!tenantId || !result) return;

      // Type guard: check if result is an Invitation or Member
      const isInvitation = 'invitationId' in result;

      if (isInvitation) {
        // Update invitations cache with the polled invitation
        qc.setQueriesData<InfiniteData<Page<Invitation>>>(
          { queryKey: memberQueryKeys.invitationListByTenant(tenantId) },
          (old) => {
            if (!old) return old;

            return {
              ...old,
              pages: old.pages.map((page, index) =>
                index === 0
                  ? {
                      ...page,
                      items: page.items.map((item, idx) =>
                        idx === 0 ? (result as Invitation) : item
                      ),
                    }
                  : page
              ),
            };
          }
        );
      } else {
        // Member was auto-accepted - invalidate members cache to refetch
        await qc.invalidateQueries({
          queryKey: memberQueryKeys.listByTenant(tenantId),
          refetchType: 'all',
        });
      }
    },
  });
}
